// Regras puras das telas de webhook do admin (croqui `screens/admin/webhooks-recebidos.tsx`
// e `webhooks-enviados.tsx`). Sem React, para o teste rodar sem provider nenhum
// (`spec/17` §11.1).
//
// `deliveryStatusPill`, `DELIVERY_TABS` e `MAX_ATTEMPTS` vêm de `features/webhooks/model.ts`
// (seller): são funções puras sobre `string`/`number` e a entrega do admin tem o mesmo
// `status` e o mesmo `attempt` da entrega do seller. Importar é leitura, não edição.
// `deliveryPill` (rótulo capitalizado do desenho antigo) foi apagado na WEB-90: as duas
// telas usam `deliveryStatusPill`, a caixa baixa do croqui.

import { errorMessageOf, isApiError } from '../../api/errors.ts';
import type { AdminDelivery, InboxItem } from '../../api/queries/admin-webhooks.ts';
import type { PillTone } from '../../components/kit/pill.tsx';
import {
  DELIVERY_STATUSES,
  DELIVERY_TABS,
  deliveryStatusPill,
  MAX_ATTEMPTS,
} from '../webhooks/model.ts';

export { DELIVERY_TABS, deliveryStatusPill, MAX_ATTEMPTS };

/**
 * Ids das abas de Enviados, na ordem do croqui. Existe separado de `DELIVERY_TABS` porque
 * `z.enum` da rota precisa de uma tupla de strings, e derivá-la com `as` escaparia do tipo
 * (`CLAUDE.md` proíbe). `all` é a ausência de `?status=` no servidor.
 */
export const DELIVERY_TAB_IDS = ['all', ...DELIVERY_STATUSES] as const;

export type DeliveryTabId = (typeof DELIVERY_TAB_IDS)[number];

export interface InboxState {
  tone: PillTone;
  label: string;
}

/**
 * Os quatro estados do croqui, na ordem em que o operador decide: erro primeiro (é a fila
 * dele), pendente depois, reprocessado e processado por último.
 *
 * `processed_at` e `error` são colunas independentes, uma linha pode ter sido processada
 * e ter registrado erro, e é exatamente essa que precisa de reprocesso. `reprocessed_at`
 * (API-25) sai do `audit_log` (`webhook_inbox.reprocess`) e é o que distingue
 * "processado de primeira" de "processado depois de alguém mexer".
 */
export function inboxState(
  row: Pick<InboxItem, 'processed_at' | 'error' | 'reprocessed_at'>,
): InboxState {
  if (row.error !== null) {
    return { tone: 'bad', label: 'Com erro' };
  }
  if (row.processed_at === null) {
    return { tone: 'wait', label: 'Não processado' };
  }
  if (row.reprocessed_at !== null) {
    return { tone: 'info', label: 'Reprocessado' };
  }
  return { tone: 'ok', label: 'Processado' };
}

/**
 * Espelha a regra do servidor (`modules/webhooks-inbound/service.ts`): linha processada e
 * sem erro devolve `409 invalid_state`. O botão nasce desabilitado em vez de deixar o
 * operador descobrir pelo vermelho.
 */
export function canReprocess(row: Pick<InboxItem, 'processed_at' | 'error'>): boolean {
  return row.processed_at === null || row.error !== null;
}

/** Assinatura inválida é o que separa "o parceiro mandou" de "alguém mandou". */
export function signaturePill(valid: boolean): InboxState {
  return valid ? { tone: 'ok', label: 'válida' } : { tone: 'bad', label: 'inválida' };
}

export function attemptLabel(delivery: Pick<AdminDelivery, 'attempt'>): string {
  return `${delivery.attempt}/${MAX_ATTEMPTS}`;
}

/** `endpoint`, `postback` ou `utmify` da cobrança (`spec/07` §4.2). */
export function targetKind(delivery: Pick<AdminDelivery, 'target'>): string {
  return delivery.target;
}

/**
 * A coluna "Destino" do croqui mostra o HOST; a URL inteira (dado de outro seller) só
 * aparece no drawer. `target_url` inválida não derruba a célula: cai na string crua.
 */
export function targetHost(delivery: Pick<AdminDelivery, 'target_url'>): string {
  try {
    return new URL(delivery.target_url).host;
  } catch {
    return delivery.target_url;
  }
}

/**
 * O step-up cancelado (o operador fechou o `StepUpDialog`) volta como o `mfa_required`
 * original de `api/client.ts`. Não é erro de tela: some sem mensagem vermelha.
 */
export function isStepUpCancelled(error: unknown): boolean {
  return isApiError(error) && error.code === 'mfa_required';
}

export function isNotFound(error: unknown): boolean {
  return isApiError(error) && error.status === 404;
}

/** Decisão pelo `code`, nunca pela mensagem (`spec/17` §4.3 item 1). */
export function reprocessErrorMessage(error: unknown): string {
  if (isApiError(error) && error.code === 'invalid_state') {
    return `Este evento já foi aplicado e não registrou erro: não há o que reprocessar. ${errorMessageOf(error)}`;
  }
  return errorMessageOf(error);
}

/**
 * O `202` não muda a linha: quem limpa o `error` e grava `processed_at` é o job
 * `process-inbox` no worker (`spec/06` §4.4). A tela diz isso em vez de prometer um
 * resultado que ela ainda não tem.
 */
export const REPROCESS_QUEUED =
  'Reprocessamento enfileirado. O resultado aparece aqui quando o worker aplicar o evento.';

/** `event_type` é nulo enquanto o parse do parceiro não reconhece o corpo (`spec/06` §4.1). */
export function eventTypeLabel(row: Pick<InboxItem, 'event_type'>): string {
  return row.event_type ?? 'unknown';
}

/** JSON do parceiro formatado; corpo que não é JSON aparece cru, sem quebrar a tela. */
export function prettyBody(rawBody: string): string {
  try {
    return JSON.stringify(JSON.parse(rawBody), null, 2);
  } catch {
    return rawBody;
  }
}

/**
 * O "Resultado do processamento" do croqui, em uma linha. É derivação do que já está na
 * linha, não há endpoint de resultado.
 */
export function processingResult(
  row: Pick<InboxItem, 'processed_at' | 'error' | 'reprocessed_at'>,
  formatAt: (iso: string) => string,
): { text: string; bad: boolean } {
  if (row.error !== null) {
    return { text: `erro: ${row.error}`, bad: true };
  }
  if (row.processed_at === null) {
    return { text: 'aguardando processamento', bad: false };
  }
  if (row.reprocessed_at !== null) {
    return { text: `ok depois do reprocesso · ${formatAt(row.processed_at)}`, bad: false };
  }
  return { text: `ok · efeito aplicado em ${formatAt(row.processed_at)}`, bad: false };
}

/**
 * Abas do croqui em Webhooks recebidos. `all` é a ausência de filtro no servidor; os
 * outros dois são o `InboxFilter` de `api/queries/admin-webhooks.ts`.
 *
 * Os ids vêm primeiro, numa tupla própria, porque `z.enum` da rota precisa deles em
 * tupla, extrair de `INBOX_TABS` com `as` escaparia do tipo (`CLAUDE.md` proíbe).
 */
export const INBOX_TAB_IDS = ['all', 'unprocessed', 'error'] as const;

export type InboxTab = (typeof INBOX_TAB_IDS)[number];

export const INBOX_TABS: { id: InboxTab; label: string }[] = [
  { id: 'all', label: 'Todos' },
  { id: 'unprocessed', label: 'Não processado' },
  { id: 'error', label: 'Com erro' },
];

/**
 * Busca da barra do croqui ("Buscar por id, referência ou provider_event_id"). É CLIENTE,
 * sobre o que já foi carregado: `GET /admin/v1/webhook-inbox` não tem busca textual (só
 * `acquirer_id` e `event_type` exatos). O vazio da tabela diz isso.
 */
export function matchesInboxSearch(row: InboxItem, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (needle === '') {
    return true;
  }
  return [row.id, row.provider_event_id, row.end_to_end_id ?? '', row.event_type ?? ''].some(
    (field) => field.toLowerCase().includes(needle),
  );
}

/** Idem para "Buscar por evento, id da entrega ou seller". */
export function matchesDeliverySearch(row: AdminDelivery, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (needle === '') {
    return true;
  }
  return [row.id, row.event_type, row.seller.display_name, row.target_url].some((field) =>
    field.toLowerCase().includes(needle),
  );
}

/**
 * Opções do `Select` de seller do croqui. Saem das linhas JÁ CARREGADAS: não há endpoint
 * de "sellers com entrega" e carregar `GET /admin/v1/sellers` inteiro para preencher um
 * `<select>` custaria uma lista de milhares para filtrar oito linhas. `selected` entra na
 * lista mesmo quando o filtro já recortou tudo, senão o operador fica preso no seller
 * escolhido (registrado em "Questões abertas").
 */
export function sellerOptions(
  rows: AdminDelivery[],
  selected?: { id: string; display_name: string },
): { id: string; label: string }[] {
  const byId = new Map<string, string>();
  if (selected !== undefined) {
    byId.set(selected.id, selected.display_name);
  }
  for (const row of rows) {
    byId.set(row.seller.id, row.seller.display_name);
  }
  return [...byId]
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
